import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
const MODULE_KEY = "monthly_projection";
// ASYNC THUNKS
// Fetch the stored (scheduler / previously generated) rows for a target month.
export const fetchMonthlyProjection = createAsyncThunk("monthlyProjection/fetch", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/monthly-projection/list", payload, {
            Authorization: token,
            "x-module-key": MODULE_KEY,
        });
        const responseData = response?.data;
        if (responseData?.success)
            return responseData.data;
        return rejectWithValue(responseData?.message || "Failed to fetch monthly projection");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message || error.message || "An error occurred while fetching monthly projection");
    }
});
// Compute + store the projection for the target month, anchored at startDate ("Generate Projection").
export const generateMonthlyProjection = createAsyncThunk("monthlyProjection/generate", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/monthly-projection/generate", payload, {
            Authorization: token,
            "x-module-key": MODULE_KEY,
        });
        const responseData = response?.data;
        if (responseData?.success)
            return responseData.data;
        return rejectWithValue(responseData?.message || "Failed to generate monthly projection");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message || error.message || "An error occurred while generating monthly projection");
    }
});
// Affiliate: fetch stored rows for a target month.
export const fetchAffiliateProjection = createAsyncThunk("monthlyProjection/fetchAffiliate", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/monthly-projection/affiliate/list", payload, {
            Authorization: token,
            "x-module-key": MODULE_KEY,
        });
        const responseData = response?.data;
        if (responseData?.success)
            return responseData.data;
        return rejectWithValue(responseData?.message || "Failed to fetch affiliate projection");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message || error.message || "An error occurred while fetching affiliate projection");
    }
});
// Affiliate: compute + store the projection for the target month (no growth-rate anchor).
export const generateAffiliateProjection = createAsyncThunk("monthlyProjection/generateAffiliate", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/monthly-projection/affiliate/generate", payload, {
            Authorization: token,
            "x-module-key": MODULE_KEY,
        });
        const responseData = response?.data;
        if (responseData?.success)
            return responseData.data;
        return rejectWithValue(responseData?.message || "Failed to generate affiliate projection");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message || error.message || "An error occurred while generating affiliate projection");
    }
});
// INITIAL STATE
// Default target month = next month (mid-month runs project the following month).
const getDefaultMonth = () => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
// Default growth-rate anchor = today.
const getDefaultStartDate = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};
const initialState = {
    rows: [],
    totalSummary: null,
    pagination: null,
    lastUpdatedAt: null,
    meta: null,
    loading: false,
    generating: false,
    error: null,
    month: getDefaultMonth(),
    startDate: getDefaultStartDate(),
    page: 1,
    limit: 20,
    sortBy: "projected_revenue",
    sortOrder: "desc",
    affiliateRows: [],
    affiliateSummary: null,
    affiliatePagination: null,
    affiliateMeta: null,
    affiliateLastUpdatedAt: null,
    affiliateLoading: false,
    affiliateGenerating: false,
    affiliatePage: 1,
    affiliateLimit: 20,
    affiliateSortBy: "mc_projected_revenue",
    affiliateSortOrder: "desc",
};
// SLICE
const applyData = (state, data) => {
    state.rows = data.rows || [];
    state.totalSummary = data.total_summary || null;
    state.pagination = data.pagination || null;
    state.lastUpdatedAt = data.lastUpdatedAt || null;
    state.meta = data.meta || null;
    state.error = null;
};
const applyAffiliateData = (state, data) => {
    state.affiliateRows = data.rows || [];
    state.affiliateSummary = data.total_summary || null;
    state.affiliatePagination = data.pagination || null;
    state.affiliateLastUpdatedAt = data.lastUpdatedAt || null;
    state.affiliateMeta = data.meta || null;
    state.error = null;
};
const monthlyProjectionSlice = createSlice({
    name: "monthlyProjection",
    initialState,
    reducers: {
        setMonth: (state, action) => {
            state.month = action.payload;
            state.page = 1;
        },
        setStartDate: (state, action) => {
            state.startDate = action.payload;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setLimit: (state, action) => {
            state.limit = action.payload;
            state.page = 1;
        },
        setSortConfig: (state, action) => {
            state.sortBy = action.payload.sortBy;
            state.sortOrder = action.payload.sortOrder;
            state.page = 1;
        },
        clearMonthlyProjection: (state) => {
            state.rows = [];
            state.totalSummary = null;
            state.pagination = null;
            state.meta = null;
            state.error = null;
        },
        setAffiliatePage: (state, action) => {
            state.affiliatePage = action.payload;
        },
        setAffiliateSortConfig: (state, action) => {
            state.affiliateSortBy = action.payload.sortBy;
            state.affiliateSortOrder = action.payload.sortOrder;
            state.affiliatePage = 1;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch (stored)
            .addCase(fetchMonthlyProjection.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchMonthlyProjection.fulfilled, (state, action) => {
            state.loading = false;
            applyData(state, action.payload);
        })
            .addCase(fetchMonthlyProjection.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch monthly projection";
        })
            // Generate (compute + store)
            .addCase(generateMonthlyProjection.pending, (state) => {
            state.generating = true;
            state.error = null;
        })
            .addCase(generateMonthlyProjection.fulfilled, (state, action) => {
            state.generating = false;
            state.page = 1;
            applyData(state, action.payload);
        })
            .addCase(generateMonthlyProjection.rejected, (state, action) => {
            state.generating = false;
            state.error = action.payload || "Failed to generate monthly projection";
        })
            // Affiliate fetch (stored)
            .addCase(fetchAffiliateProjection.pending, (state) => {
            state.affiliateLoading = true;
            state.error = null;
        })
            .addCase(fetchAffiliateProjection.fulfilled, (state, action) => {
            state.affiliateLoading = false;
            applyAffiliateData(state, action.payload);
        })
            .addCase(fetchAffiliateProjection.rejected, (state, action) => {
            state.affiliateLoading = false;
            state.error = action.payload || "Failed to fetch affiliate projection";
        })
            // Affiliate generate (compute + store)
            .addCase(generateAffiliateProjection.pending, (state) => {
            state.affiliateGenerating = true;
            state.error = null;
        })
            .addCase(generateAffiliateProjection.fulfilled, (state, action) => {
            state.affiliateGenerating = false;
            state.affiliatePage = 1;
            applyAffiliateData(state, action.payload);
        })
            .addCase(generateAffiliateProjection.rejected, (state, action) => {
            state.affiliateGenerating = false;
            state.error = action.payload || "Failed to generate affiliate projection";
        });
    },
});
// EXPORTS
export const { setMonth, setStartDate, setPage, setLimit, setSortConfig, clearMonthlyProjection, setAffiliatePage, setAffiliateSortConfig, } = monthlyProjectionSlice.actions;
// Selectors
export const selectMonthlyProjectionRows = (state) => state.monthlyProjection.rows;
export const selectMonthlyProjectionLoading = (state) => state.monthlyProjection.loading;
export const selectMonthlyProjectionGenerating = (state) => state.monthlyProjection.generating;
export const selectMonthlyProjectionError = (state) => state.monthlyProjection.error;
export const selectMonthlyProjectionSummary = (state) => state.monthlyProjection.totalSummary;
export const selectMonthlyProjectionPagination = (state) => state.monthlyProjection.pagination;
export const selectMonthlyProjectionMeta = (state) => state.monthlyProjection.meta;
export const selectMonthlyProjectionLastUpdatedAt = (state) => state.monthlyProjection.lastUpdatedAt;
export default monthlyProjectionSlice.reducer;
